import { tryGetShopifyIdToken } from './app-bridge';

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

export type AuthMode = 'embedded' | 'standalone';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
  }
}

export type ExtraHeaders = Record<string, string | number | null | undefined>;

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  mode: AuthMode;
  signal?: AbortSignal;
  headers?: ExtraHeaders;
};

function mergeHeaders(base: Record<string, string>, extra?: ExtraHeaders): Record<string, string> {
  if (!extra) return base;
  const out = { ...base };
  for (const [key, value] of Object.entries(extra)) {
    if (value === null || value === undefined) continue;
    out[key] = String(value);
  }
  return out;
}

async function buildHeaders(opts: RequestOptions, accept: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = { Accept: accept };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.mode === 'embedded') {
    const token = await tryGetShopifyIdToken();
    if (!token) throw new ApiError(401, null, 'Shopify session token unavailable');
    headers.Authorization = `Bearer ${token}`;
  }
  return mergeHeaders(headers, opts.headers);
}

async function request<T>(path: string, opts: RequestOptions): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const headers = await buildHeaders(opts, 'application/json');

  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: opts.mode === 'standalone' ? 'include' : 'omit',
    signal: opts.signal,
  });

  if (response.status === 204) return undefined as T;

  const contentType = response.headers.get('content-type') ?? '';
  const payload = contentType.includes('json')
    ? await response.json().catch(() => null)
    : await response.text();

  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

async function requestBlob(path: string, opts: RequestOptions): Promise<Blob> {
  const url = `${BASE_URL}${path}`;
  // Accept JSON so the backend renders errors as an RFC7807 problem (which needs
  // `expectsJson()`), not an HTML error page. A successful download streams its own
  // binary Content-Type regardless - the server doesn't content-negotiate here.
  const headers = await buildHeaders(opts, 'application/json');

  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: opts.mode === 'standalone' ? 'include' : 'omit',
    signal: opts.signal,
  });

  if (!response.ok) {
    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('json')
      ? await response.json().catch(() => null)
      : await response.text().catch(() => null);
    throw new ApiError(response.status, payload);
  }

  return response.blob();
}

export const api = {
  get: <T>(path: string, mode: AuthMode, signal?: AbortSignal, headers?: ExtraHeaders) =>
    request<T>(path, { method: 'GET', mode, signal, headers }),
  post: <T>(
    path: string,
    body: unknown,
    mode: AuthMode,
    signal?: AbortSignal,
    headers?: ExtraHeaders,
  ) => request<T>(path, { method: 'POST', body, mode, signal, headers }),
  put: <T>(
    path: string,
    body: unknown,
    mode: AuthMode,
    signal?: AbortSignal,
    headers?: ExtraHeaders,
  ) => request<T>(path, { method: 'PUT', body, mode, signal, headers }),
  patch: <T>(
    path: string,
    body: unknown,
    mode: AuthMode,
    signal?: AbortSignal,
    headers?: ExtraHeaders,
  ) => request<T>(path, { method: 'PATCH', body, mode, signal, headers }),
  del: <T>(path: string, mode: AuthMode, signal?: AbortSignal, headers?: ExtraHeaders) =>
    request<T>(path, { method: 'DELETE', mode, signal, headers }),
  blob: (path: string, mode: AuthMode, signal?: AbortSignal, headers?: ExtraHeaders) =>
    requestBlob(path, { method: 'GET', mode, signal, headers }),
};
